/** Sieve — shared game types (§3, §8, §9; Multiples spec §9). */

import type { Puzzle } from '../engine/grid';
import type { MultiplesPuzzle } from '../engine/grid-multiples';

export type { Puzzle, MultiplesPuzzle };

/** The two dailies. Each has its own puzzle, streak and stats (Multiples §2). */
export type GameMode = 'prime' | 'multiples';

/**
 * A puzzle normalised for the mode-agnostic game/UI layer. Both the prime
 * Puzzle (targets = primeIndices) and the MultiplesPuzzle (targets =
 * targetIndices) map onto this. `divisor` is present only for Multiples.
 */
export interface PlayPuzzle {
  mode: GameMode;
  id: number;
  date: string;
  grid: number[];
  targetIndices: number[];
  divisor?: number;
}

export function normalizePrime(p: Puzzle): PlayPuzzle {
  return { mode: 'prime', id: p.id, date: p.date, grid: p.grid, targetIndices: p.primeIndices };
}

export function normalizeMultiples(p: MultiplesPuzzle): PlayPuzzle {
  return {
    mode: 'multiples',
    id: p.id,
    date: p.date,
    grid: p.grid,
    targetIndices: p.targetIndices,
    divisor: p.divisor,
  };
}

/** Outcome tiers (§3.1). */
export type Outcome = 'flawless' | 'solved' | 'failed';

/** What happened to a single tap. */
export type TapResult = 'correct' | 'wrong';

/** Ordered log of tap results — this is what the share string encodes (§7). */
export type TapLog = TapResult[];

/** Status of a play session. */
export type PlayStatus = 'playing' | 'won' | 'lost';

/** Live, in-memory game state for one puzzle (§3). */
export interface GameState {
  puzzle: PlayPuzzle;
  /** Grid indices the player has committed, in tap order. */
  taps: number[];
  /** Parallel to `taps`: was each tap correct or wrong? */
  tapResults: TapLog;
  /** Count of targets found (primes, or multiples of X). */
  found: number;
  mistakes: number;
  status: PlayStatus;
}

/** Persisted per-attempt progress so a reload resumes mid-grid (§9). */
export interface InProgress {
  puzzleId: number;
  taps: number[];
  mistakes: number;
}

/** Aggregate stats (§8). mistakeHistogram is [0,1,2,failed] counts. */
export interface Stats {
  played: number;
  won: number;
  flawless: number;
  currentStreak: number;
  maxStreak: number;
  mistakeHistogram: [number, number, number, number];
}

/**
 * A completed puzzle is stored as its ordered tap indices; everything else
 * (outcome, mistakes, glyph line) is re-derived by replaying against the puzzle,
 * keeping one source of truth (§8, §9).
 */
export type CompletedResult = number[];

export type LanguagePref = 'auto' | 'en' | 'fr';

export interface Settings {
  language: LanguagePref;
  highContrast: boolean;
  colorBlind: boolean;
  reducedMotion: boolean;
}

/** Per-mode persisted block. Prime and Multiples each own one (Multiples §8). */
export interface ModeState {
  lastPuzzleId: number;
  /** Id of the last daily puzzle whose result was already applied to stats. */
  lastCompletedDailyId: number;
  stats: Stats;
  inProgress: InProgress | null;
  /** id → tap indices for each completed DAILY puzzle (drives re-display, §9). */
  dailyResults: Record<number, CompletedResult>;
  /** id → tap indices for each completed ARCHIVE replay (no stats effect, §8). */
  archiveResults: Record<number, CompletedResult>;
}

/** The full persisted document (§9). Versioned with a migration path. */
export interface PersistedState {
  schemaVersion: number;
  /** Which daily the app last opened (Multiples is the default for new users). */
  lastMode: GameMode;
  prime: ModeState;
  multiples: ModeState;
  /** Rapid best is shared across both dailies. */
  rapidBest: number;
  settings: Settings;
}

export function emptyStats(): Stats {
  return {
    played: 0,
    won: 0,
    flawless: 0,
    currentStreak: 0,
    maxStreak: 0,
    mistakeHistogram: [0, 0, 0, 0],
  };
}

export function emptyModeState(): ModeState {
  return {
    lastPuzzleId: 0,
    lastCompletedDailyId: 0,
    stats: emptyStats(),
    inProgress: null,
    dailyResults: {},
    archiveResults: {},
  };
}

export function defaultState(): PersistedState {
  return {
    schemaVersion: 2,
    lastMode: 'multiples',
    prime: emptyModeState(),
    multiples: emptyModeState(),
    rapidBest: 0,
    settings: { language: 'auto', highContrast: false, colorBlind: false, reducedMotion: false },
  };
}
